import type { TFunction } from "i18next";
import React from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import type { ProductReference } from "../../types/types.PIM";
import { ProductReferenceMockTooltip } from "../ProductReferenceMockTooltip/ProductReferenceMockTooltip";
import { getProductReferenceHref } from "../ProductReference/ProductReferenceAttributeDisplay";
import { MultiProductReferenceTooltip } from "./MultiProductReferenceTooltip";

export type CollectionReferenceCellProps = {
  references: ProductReference[];
  isPortfolio: boolean;
  storePath: string;
  adminPath: string;
  isAccessible: (ref: ProductReference) => boolean;
  getRestrictedTooltip: (ref: ProductReference) => string;
  renderAccessibleRef?: (
    ref: ProductReference,
    key: string,
    link: React.ReactElement
  ) => React.ReactElement;
  renderMultiReference?: (references: ProductReference[]) => React.ReactElement;
  onMoreClick?: () => void;
  t: TFunction;
};

export function CollectionReferenceCell({
  references,
  isPortfolio,
  storePath,
  adminPath,
  isAccessible,
  getRestrictedTooltip,
  renderAccessibleRef,
  renderMultiReference,
  onMoreClick,
}: CollectionReferenceCellProps): React.ReactElement | null {
  if (!references.length) return <>{"--"}</>;

  const buildLink = (reference: ProductReference): React.ReactElement => (
    <Link
      to={getProductReferenceHref(reference, {
        isPortfolio,
        storePath,
        adminPath,
      })}
    >
      {reference.name}
    </Link>
  );

  const renderSingleReference = (
    reference: ProductReference,
    key: string
  ): React.ReactElement => {
    if (isAccessible(reference)) {
      if (renderAccessibleRef) {
        return renderAccessibleRef(reference, key, buildLink(reference));
      }
      return (
        <ProductReferenceMockTooltip
          key={key}
          productRef={reference}
          isPortfolio={isPortfolio}
          storePath={storePath}
          adminPath={adminPath}
        >
          {buildLink(reference)}
        </ProductReferenceMockTooltip>
      );
    }
    return (
      <span
        key={key}
        data-tip={getRestrictedTooltip(reference)}
        data-for={reference.id}
      >
        {reference.name}
        <ReactTooltip delayHide={200} id={reference.id} effect="solid" />
      </span>
    );
  };

  if (references.length === 1) {
    return renderSingleReference(references[0], "ref-0");
  }

  if (renderMultiReference) {
    return renderMultiReference(references);
  }

  return (
    <MultiProductReferenceTooltip
      references={references}
      isPortfolio={isPortfolio}
      storePath={storePath}
      adminPath={adminPath}
      isAccessible={isAccessible}
      getRestrictedTooltip={getRestrictedTooltip}
      onMoreClick={onMoreClick}
    />
  );
}
